import React, { Children, cloneElement, useEffect, useMemo, useRef, useState, useContext } from 'react';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'motion/react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { House, Trophy, History, ChartColumnIncreasing, User, WalletCards, LogOut, Menu, X, Sun, Moon, UserPlus } from 'lucide-react';
import { UserContext } from '../UserContext';
import { ThemeContext } from '../ThemeContext';
import { NavigationContext } from './NavigationContext';
import { useAnnouncementBar } from './AnnouncementBarContext';
import api from '../api';
import { clearAuthStorage } from '../authStorage';
import GameYourSatLogo from '../img/GameYourSatLogo.png';

function DockItem({ children, className = '', onClick, mouseY, spring, distance, magnification, baseItemSize, isMobile = false }) {
  const ref = useRef(null);
  const isHovered = useMotionValue(0);
  const [isTouched, setIsTouched] = useState(false);
  const touchHandledRef = useRef(false);

  const mouseDistance = useTransform(mouseY, (val) => {
    if (!ref.current || val === Infinity) return distance + 1;
    const rect = ref.current.getBoundingClientRect();
    const centerY = rect.top + rect.height / 2;
    const dist = Math.abs(val - centerY);
    return dist;
  });

  const targetSize = useTransform(mouseDistance, (dist) => {
    if (dist > distance) return baseItemSize;
    const normalizedDist = Math.min(dist / distance, 1);
    const scale = 1 - normalizedDist;
    const size = baseItemSize + (magnification - baseItemSize) * scale;
    return Math.max(baseItemSize, Math.min(magnification, size));
  });

  const size = useSpring(targetSize, { 
    stiffness: 400,
    damping: 30,
    mass: 0.5
  });

  // On mobile, use touch state for hover effect
  useEffect(() => {
    if (isTouched) {
      isHovered.set(1);
      const timer = setTimeout(() => {
        isHovered.set(0);
        setIsTouched(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isTouched, isHovered]);

  return (
    <motion.div
      ref={ref}
      style={{
        width: size,
        height: size,
        pointerEvents: 'auto'
      }}
      onHoverStart={() => !isMobile && isHovered.set(1)}
      onHoverEnd={() => !isMobile && isHovered.set(0)}
      onTouchStart={(e) => {
        e.stopPropagation();
        if (isMobile) {
          touchHandledRef.current = false;
          setIsTouched(true);
          isHovered.set(1);
        }
      }}
      onTouchEnd={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (isMobile && !touchHandledRef.current) {
          touchHandledRef.current = true;
          // Trigger click on touch end for mobile
          if (onClick) {
            const syntheticEvent = {
              ...e,
              stopPropagation: () => {},
              preventDefault: () => {}
            };
            onClick(syntheticEvent);
          }
          setTimeout(() => {
            isHovered.set(0);
            setIsTouched(false);
            touchHandledRef.current = false;
          }, 300);
        }
      }}
      onFocus={() => isHovered.set(1)}
      onBlur={() => isHovered.set(0)}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        // Skip if touch event already handled it (mobile)
        if (isMobile && touchHandledRef.current) {
          return;
        }
        if (onClick) {
          onClick(e);
        }
      }}
      className={`relative inline-flex items-center justify-center rounded-full bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 shadow-md hover:shadow-lg active:scale-95 transition-shadow cursor-pointer touch-manipulation ${className}`}
      tabIndex={0}
      role="button"
      aria-haspopup="true"
    >
      {Children.map(children, child => cloneElement(child, { isHovered }))}
    </motion.div>
  );
}

function DockLabel({ children, className = '', extraMargin = false, ...rest }) {
  const { isHovered } = rest;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isHovered) return;
    const unsubscribe = isHovered.on('change', latest => {
      setIsVisible(latest === 1);
    });
    return () => unsubscribe();
  }, [isHovered]);

  // On mobile, show labels when hovered/touched
  const marginClass = extraMargin ? 'ml-12 md:ml-14' : 'ml-2 md:ml-3';
  
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 10 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.2 }}
          className={`${className} absolute left-full top-1/2 ${marginClass} w-fit whitespace-nowrap rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-800 dark:text-gray-200 shadow-lg z-50`}
          role="tooltip"
          style={{ y: '-50%' }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DockIcon({ children, className = '' }) {
  return (
    <div className={`flex items-center justify-center text-gray-700 dark:text-gray-300 ${className}`}>
      {children}
    </div>
  );
}

export default function Dock({
  className = '',
  spring = { mass: 0.1, stiffness: 150, damping: 12 },
  magnification = 70,
  distance = 200,
  panelHeight = 64,
  dockHeight = 256,
  baseItemSize = 50
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useContext(UserContext);
  const { isDarkMode, toggleDarkMode } = useContext(ThemeContext);
  const { setPendingNavigation, setPromptEndSession, promptLogout, setPromptLogout } = useContext(NavigationContext);
  const { isVisible: announcementBarVisible, height: announcementBarHeight } = useAnnouncementBar();
  
  // Total score state
  const [totalScore, setTotalScore] = useState(0);
  
  // Memoize gradient for section headings
  const sectionHeadingGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);
  const mouseY = useMotionValue(Infinity);
  const isHovered = useMotionValue(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);
  const [isCompactPreLoginHeader, setIsCompactPreLoginHeader] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopBottomDockOpen, setIsDesktopBottomDockOpen] = useState(false);
  const [isPreLoginMobileMenuOpen, setIsPreLoginMobileMenuOpen] = useState(false);

  // Calculate top offset based on announcement bar
  // Mobile: no base gap, only when announcement bar is visible
  // Desktop: 10px base gap, plus announcement bar height when visible
  const topOffset = useMemo(() => {
    const baseGap = isMobile ? 0 : 10;
    return (announcementBarVisible ? announcementBarHeight : 0) + baseGap;
  }, [isMobile, announcementBarVisible, announcementBarHeight]);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsSmallMobile(width < 480); // Very small screens (iPhone SE, etc.)
      setIsCompactPreLoginHeader(width < 1100);
      // Close menu when switching from mobile to desktop
      if (width >= 768) {
        setIsMobileMenuOpen(false);
      }
      if (width >= 1100) {
        setIsPreLoginMobileMenuOpen(false);
      }
      // Close desktop bottom dock when switching to mobile
      if (width < 768) {
        setIsDesktopBottomDockOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu when clicking outside on mobile
  useEffect(() => {
    if (!isMobileMenuOpen || !isMobile) return;
    
    const handleClickOutside = (event) => {
      const dockElement = document.querySelector('[data-dock-container]');
      const menuButton = document.querySelector('[data-menu-button]');
      
      if (dockElement && menuButton && 
          !dockElement.contains(event.target) && 
          !menuButton.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileMenuOpen, isMobile]);


  const handleSmartNav = (path) => {
    if (location.pathname.startsWith("/play-game")) {
      setPendingNavigation(path);
      setPromptEndSession(true);
    } else {
      navigate(path);
    }
  };

  const handleLogout = () => {
    if (location.pathname.startsWith("/play-game")) {
      setPendingNavigation("_logout_");
      setPromptEndSession(true);
    } else {
      setPromptLogout(true);
    }
  };

  const confirmLogout = () => {
    clearAuthStorage();
    setUser(null);
    setPromptLogout(false);
    navigate("/");
  };

  // Define main navigation items (top dock)
  const navigationItems = user ? [
    { 
      icon: <House size={20} />, 
      label: 'Home', 
      onClick: () => handleSmartNav("/options"),
      active: location.pathname === '/options'
    },
    { 
      icon: <Trophy size={20} />, 
      label: 'Leaderboard', 
      onClick: () => handleSmartNav("/leaderboard"),
      active: location.pathname === '/leaderboard'
    },
    { 
      icon: <History size={20} />, 
      label: 'History', 
      onClick: () => handleSmartNav("/answer-history"),
      active: location.pathname === '/answer-history'
    },
    { 
      icon: <ChartColumnIncreasing size={20} />, 
      label: 'Analytics', 
      onClick: () => handleSmartNav("/analytics"),
      active: location.pathname === '/analytics'
    },
  ] : [];

  // Define bottom navigation items (Profile, Referral, Subscription, Logout)
  const bottomNavigationItems = user ? [
    { 
      icon: <User size={20} />, 
      label: 'Profile', 
      onClick: () => handleSmartNav("/profile"),
      active: location.pathname === '/profile'
    },
    { 
      icon: <UserPlus size={20} />, 
      label: 'Referral', 
      onClick: () => handleSmartNav("/refer"),
      active: location.pathname === '/refer'
    },
    { 
      icon: <WalletCards size={20} />, 
      label: 'Subscription', 
      onClick: () => handleSmartNav("/subscription"),
      active: location.pathname === '/subscription'
    },
    { 
      icon: <LogOut size={20} />, 
      label: 'Logout', 
      onClick: handleLogout,
      className: 'hover:bg-red-100 dark:hover:bg-red-900'
    },
  ] : [];

  // Bottom dock state
  const bottomMouseY = useMotionValue(Infinity);
  const bottomIsHovered = useMotionValue(0);

  // Get user screenname with fallbacks
  const userScreenName = useMemo(() => {
    if (!user) return '';
    const nameCandidates = [user.screenName, user.screen_name, user.user_name, user.username, user.name];
    const chosen = nameCandidates.find(Boolean);
    if (chosen) return chosen;
    if (user.email && typeof user.email === 'string') {
      const local = user.email.split('@')[0];
      if (local) return local;
    }
    return 'User';
  }, [user]);

  // Define paths where the Dock should NOT be displayed
  const noDockPaths = ['/login', '/signup', '/forgot-password', '/', '/sat-test'];
  const shouldShowDock = user && !noDockPaths.includes(location.pathname);
  
  // Define pre-login paths where only logo should be shown
  const preLoginPaths = ['/login', '/signup', '/forgot-password', '/', '/contact-us', '/terms', '/privacy', '/about-mety-technology', '/about-the-game', '/sat', '/pricing'];
  const isPreLoginPage = !user && preLoginPaths.includes(location.pathname);
  
  // Define paths where total score should NOT be displayed
  const noTotalScorePaths = ['/play-game', '/sat-test'];
  const isMultiplayerGameRoom = location.pathname.startsWith('/multiplayer/game/');
  const shouldShowTotalScore = user && shouldShowDock && !noTotalScorePaths.includes(location.pathname) && !isMultiplayerGameRoom;
  
  // Fetch total score
  useEffect(() => {
    const fetchTotalScore = async () => {
      if (user?.user_id) {
        try {
          const response = await api.get(`/api/user/get_total_score/`);
          setTotalScore(response.data?.total_score || 0);
        } catch (error) {
          console.error('Error fetching total score:', error);
          setTotalScore(0);
        }
      } else {
        setTotalScore(0);
      }
    };
    
    if (shouldShowTotalScore && user?.is_premium) {
      fetchTotalScore();
    }
  }, [user?.user_id, user?.is_premium, shouldShowTotalScore]);

  // Show logo on pre-login pages even if dock is not shown
  if (!shouldShowDock && !isPreLoginPage) {
    return null; // Don't show dock if user is not logged in or on excluded paths
  }

  return (
    <>
      {/* Header for Pre-Login Pages */}
      {isPreLoginPage && (
        <>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed top-0 left-0 right-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 shadow-md"
              style={{ 
                paddingTop: `${topOffset}px`,
                minHeight: `${topOffset + (isCompactPreLoginHeader ? 80 : 70)}px`
              }}
            >
              <div className="w-full py-3 md:py-4">
                {/* Mobile: Menu Button */}
              {isCompactPreLoginHeader && (
                <div className="flex items-center justify-between px-4">
                  <motion.button
                    onClick={() => setIsPreLoginMobileMenuOpen(!isPreLoginMobileMenuOpen)}
                    className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 border border-gray-300 dark:border-gray-600 shadow-md flex items-center justify-center"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Toggle menu"
                  >
                    <AnimatePresence mode="wait">
                      {isPreLoginMobileMenuOpen ? (
                        <motion.div
                          key="close"
                          initial={{ rotate: -90, opacity: 0 }}
                          animate={{ rotate: 0, opacity: 1 }}
                          exit={{ rotate: 90, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <X size={24} className="text-gray-700 dark:text-gray-300" />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="menu"
                          initial={{ rotate: 90, opacity: 0 }}
                          animate={{ rotate: 0, opacity: 1 }}
                          exit={{ rotate: -90, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Menu size={24} className="text-gray-700 dark:text-gray-300" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.button>
                  <img 
                    src={GameYourSatLogo} 
                    alt="GameYourSAT Logo" 
                    onClick={() => navigate('/')}
                    className="h-6 w-auto object-contain cursor-pointer transition-opacity hover:opacity-80 flex-shrink-0"
                    draggable={false}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate('/');
                      }
                    }}
                    aria-label="Go to home"
                  />
                  <div className="w-10"></div> {/* Spacer for centering */}
                </div>
              )}

              {/* Desktop: Full Header */}
              <div className="hidden xl:flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                {/* Left side: Logo and Navigation Links - Aligned to left edge */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 md:gap-6 pl-4 sm:pl-6 lg:pl-8">
                  <img 
                    src={GameYourSatLogo} 
                    alt="GameYourSAT Logo" 
                    onClick={() => navigate('/')}
                    className="h-6 w-auto md:h-8 object-contain cursor-pointer transition-opacity hover:opacity-80 flex-shrink-0"
                    draggable={false}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate('/');
                      }
                    }}
                    aria-label="Go to home"
                  />
                  {/* Links beside logo */}
                  <div className="flex flex-row items-center gap-3 sm:gap-4 md:gap-6 lg:gap-8 flex-wrap">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link
                        to="/sat"
                        className="text-sm sm:text-base md:text-lg lg:text-xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200 hover:text-[#06bae4] dark:hover:text-[#06bae4] transition-colors whitespace-nowrap"
                      >
                        SAT
                      </Link>
                    </motion.div>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link
                        to="/about-the-game"
                        className="text-sm sm:text-base md:text-lg lg:text-xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200 hover:text-[#06bae4] dark:hover:text-[#06bae4] transition-colors whitespace-nowrap"
                      >
                        About The Game
                      </Link>
                    </motion.div>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link
                        to="/pricing"
                        className="text-sm sm:text-base md:text-lg lg:text-xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200 hover:text-[#06bae4] dark:hover:text-[#06bae4] transition-colors whitespace-nowrap"
                      >
                        Pricing
                      </Link>
                    </motion.div>
                  </div>
                </div>
                
                {/* Right side: Sign Up and Login buttons */}
                <div className="flex items-center gap-2 sm:gap-3 md:gap-4 pr-16 sm:pr-20 md:pr-24 lg:pr-28 flex-shrink-0">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      to="/signup"
                      className="inline-block px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-full font-bold font-zonaProUltra text-lg md:text-xl transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                    >
                      Sign Up
                    </Link>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      to="/login"
                      className="inline-block px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 bg-[#212D82] dark:bg-[#ff7a00] hover:bg-[#1a2469] dark:hover:bg-[#e66900] text-white rounded-full font-bold font-zonaProUltra text-lg md:text-xl transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                    >
                      Login
                    </Link>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Mobile Menu Dropdown */}
          {isCompactPreLoginHeader && (
            <AnimatePresence>
              {isPreLoginMobileMenuOpen && (
                <>
                  {/* Backdrop */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30"
                    onClick={() => setIsPreLoginMobileMenuOpen(false)}
                  />
                  {/* Menu */}
                  <motion.div
                    initial={{ opacity: 0, x: -300 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -300 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="fixed top-0 left-0 bottom-0 z-40 w-64 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-r border-gray-200 dark:border-gray-700 shadow-xl"
                      style={{ 
                        paddingTop: `${topOffset + (isCompactPreLoginHeader ? 80 : 70)}px`
                      }}
                    >
                    <div className="flex flex-col p-4 gap-4 overflow-y-auto h-full">
                      {/* Navigation Links */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="flex flex-col gap-3"
                      >
                        <Link
                          to="/sat"
                          onClick={() => setIsPreLoginMobileMenuOpen(false)}
                          className="text-lg font-bold font-zonaProUltra text-gray-800 dark:text-gray-200 hover:text-[#06bae4] dark:hover:text-[#06bae4] transition-colors py-2 px-4 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          SAT
                        </Link>
                        <Link
                          to="/about-the-game"
                          onClick={() => setIsPreLoginMobileMenuOpen(false)}
                          className="text-lg font-bold font-zonaProUltra text-gray-800 dark:text-gray-200 hover:text-[#06bae4] dark:hover:text-[#06bae4] transition-colors py-2 px-4 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          About The Game
                        </Link>
                        <Link
                          to="/pricing"
                          onClick={() => setIsPreLoginMobileMenuOpen(false)}
                          className="text-lg font-bold font-zonaProUltra text-gray-800 dark:text-gray-200 hover:text-[#06bae4] dark:hover:text-[#06bae4] transition-colors py-2 px-4 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          Pricing
                        </Link>
                      </motion.div>

                      {/* Divider */}
                      <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>

                      {/* Sign Up and Login Buttons */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex flex-col gap-3"
                      >
                        <Link
                          to="/signup"
                          onClick={() => setIsPreLoginMobileMenuOpen(false)}
                          className="inline-block w-full text-center px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-full font-bold font-zonaProUltra text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                          Sign Up
                        </Link>
                        <Link
                          to="/login"
                          onClick={() => setIsPreLoginMobileMenuOpen(false)}
                          className="inline-block w-full text-center px-6 py-3 bg-[#212D82] dark:bg-[#ff7a00] hover:bg-[#1a2469] dark:hover:bg-[#e66900] text-white rounded-full font-bold font-zonaProUltra text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                          Login
                        </Link>
                      </motion.div>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          )}
        </>
      )}

      {/* Dark Mode Toggle for Pre-Login Pages */}
      {isPreLoginPage && (
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, type: "spring" }}
          className={`fixed right-2 ${isMobile ? 'md:right-10' : 'md:right-10'} z-40 pointer-events-auto`}
          style={{ top: `${8 + topOffset}px` }}
        >
          <motion.button
            onClick={toggleDarkMode}
            whileHover={{
              scale: 1.05,
              y: -2,
              transition: { duration: 0.2 },
            }}
            whileTap={{ scale: 0.95 }}
            className="relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-xl shadow-2xl p-3 sm:p-4 border transition-all duration-300 flex items-center justify-center"
            style={{
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
              borderColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
            }}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? (
              <Sun size={20} className="text-[#ff7a00]" />
            ) : (
              <Moon size={20} className="text-[#212D82]" />
            )}
          </motion.button>
        </motion.div>
      )}

      {!shouldShowDock && isPreLoginPage ? null : (
        <>
      {/* Mobile Menu Button - Top Left */}
      {isMobile && (
        <motion.button
          data-menu-button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="fixed left-2 z-50 p-1.5 rounded-lg bg-white/95 dark:bg-gray-800/95 border-2 border-gray-300 dark:border-gray-600 shadow-lg backdrop-blur-md flex items-center justify-center"
          style={{ top: `${8 + topOffset}px` }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Toggle menu"
        >
          <AnimatePresence mode="wait">
            {isMobileMenuOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <X size={28} className="text-gray-700 dark:text-gray-300" />
              </motion.div>
            ) : (
              <motion.div
                key="menu"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Menu size={28} className="text-gray-700 dark:text-gray-300" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      )}

      {/* Logo - Top Left (Above Screenname) - Only show when logged in */}
      {shouldShowDock && (
        <div
          className={`fixed ${isMobile ? 'left-16' : 'left-2 md:left-22'} z-40 hidden md:block`}
          style={{ top: `${8 + topOffset}px` }}
        >
          <img 
            src={GameYourSatLogo} 
            alt="GameYourSAT Logo" 
            onClick={() => handleSmartNav("/options")}
            className="h-5 w-auto md:h-6 object-contain cursor-pointer transition-opacity hover:opacity-80"
            draggable={false}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSmartNav("/options");
              }
            }}
            aria-label="Go to home"
          />
        </div>
      )}


      {/* Total Score Display - Top Right Corner */}
      {shouldShowTotalScore && user?.is_premium && (
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, type: "spring" }}
          className={`fixed right-2 ${isMobile ? 'md:right-12' : 'md:right-12'} z-40 pointer-events-auto`}
          style={{ top: `${8 + topOffset}px` }}
        >
          <motion.div
            whileHover={{
              scale: 1.02,
              y: -2,
              transition: { duration: 0.2 },
            }}
            className="relative right-8 md:right-0 backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-lg sm:rounded-xl shadow-2xl p-0.5 sm:p-4 border transition-all duration-300"
            style={{
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
              borderColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
            }}
          >
            <div className="flex flex-row sm:flex-col items-center gap-1.5 sm:gap-2">
              <span className="text-gray-500 dark:text-gray-400 text-[9px] sm:text-xs uppercase tracking-wider font-zonaProRegular sm:text-center whitespace-nowrap">
                Total Score
              </span>
              <motion.p
                className="text-base sm:text-2xl md:text-3xl font-bold font-zonaProUltra"
                style={{ color: '#ff7a00' }}
                key={totalScore}
                initial={{ scale: 1.2, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {totalScore.toLocaleString()}
              </motion.p>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Dark Mode Toggle - Below Total Score (or Top Right if no total score) */}
      {shouldShowDock && (
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, type: "spring" }}
            className={`fixed right-2 ${shouldShowTotalScore && user?.is_premium 
            ? (isMobile ? 'md:right-10' : 'md:right-10') 
            : (isMobile ? 'md:right-10' : 'md:right-10')} z-40 pointer-events-auto`}
            style={{ 
              top: `${shouldShowTotalScore && user?.is_premium 
                ? (isMobile ? 80 + topOffset : 112 + topOffset) 
                : topOffset}px` 
            }}
        >
          <motion.button
            onClick={toggleDarkMode}
            whileHover={{
              scale: 1.05,
              y: -2,
              transition: { duration: 0.2 },
            }}
            whileTap={{ scale: 0.95 }}
            className="relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-xl shadow-2xl p-3 sm:p-4 border transition-all duration-300 flex items-center justify-center"
            style={{
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
              borderColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
            }}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? (
              <Sun size={20} className="text-[#ff7a00]" />
            ) : (
              <Moon size={20} className="text-[#212D82]" />
            )}
          </motion.button>
        </motion.div>
      )}


      {/* Mobile Overlay Backdrop */}
      <AnimatePresence>
        {isMobile && isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 dark:bg-black/70 z-40"
            onClick={(e) => {
              // Only close if clicking directly on the overlay, not on dock items
              const dockContainer = e.target.closest('[data-dock-container]');
              if (!dockContainer) {
                setIsMobileMenuOpen(false);
              }
            }}
            onTouchStart={(e) => {
              // Prevent overlay from closing when touching dock items
              const dockContainer = e.target.closest('[data-dock-container]');
              if (dockContainer) {
                e.stopPropagation();
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Main Dock - Top Left */}
      {(!isMobile || isMobileMenuOpen) && (
        <motion.div 
          className="fixed left-0 top-0 bottom-0 flex items-center justify-center z-50 pointer-events-none"
          data-dock-container
          initial={isMobile ? { x: -300 } : false}
          animate={isMobile ? { x: isMobileMenuOpen ? 0 : -300 } : { x: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <motion.div
            onMouseMove={({ pageY }) => {
              if (!isMobile) {
                isHovered.set(1);
                mouseY.set(pageY);
              }
            }}
            onMouseLeave={() => {
              if (!isMobile) {
                isHovered.set(0);
                mouseY.set(Infinity);
              }
            }}
            className={`${className} pointer-events-auto flex flex-col items-center ${isSmallMobile ? 'gap-1' : 'gap-2'} md:gap-3 rounded-r-2xl md:rounded-2xl border-r-2 md:border-2 border-gray-300 dark:border-gray-600 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md ${isSmallMobile ? 'py-1 px-0.5' : 'py-2 px-1'} md:py-4 md:px-2 shadow-xl md:ml-2 transition-all duration-300 overflow-visible`}
            style={{ 
              width: isMobile ? (isSmallMobile ? 48 : Math.max(panelHeight * 0.9, 60)) : panelHeight,
              minWidth: isMobile ? (isSmallMobile ? 48 : Math.max(panelHeight * 0.9, 60)) : panelHeight
            }}
            role="toolbar"
            aria-label="Application dock"
          >
            {navigationItems.map((item, index) => (
              <DockItem
                key={index}
                onClick={() => {
                  item.onClick();
                  if (isMobile) {
                    setIsMobileMenuOpen(false);
                  }
                }}
                className={item.className || (item.active ? `ring-2 ${isDarkMode ? 'ring-[#ff7a00]' : 'ring-[#212D82]'}` : '')}
                mouseY={mouseY}
                spring={spring}
                distance={distance}
                magnification={isMobile ? (isSmallMobile ? baseItemSize * 1.2 : baseItemSize * 1.35) : magnification}
                baseItemSize={isMobile ? (isSmallMobile ? 38 : Math.max(baseItemSize * 0.9, 50)) : baseItemSize}
                isMobile={isMobile}
              >
                <DockIcon>{item.icon}</DockIcon>
                <DockLabel>{item.label}</DockLabel>
              </DockItem>
            ))}
          </motion.div>
        </motion.div>
      )}

      {/* User Screenname Display - Above Menu Button (Desktop) or Between Docks (Mobile) */}
      {userScreenName && (
        <>
          {/* Desktop: Above Menu Button */}
          {!isMobile && shouldShowDock && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ 
                opacity: 1, 
                y: 0,
              }}
              transition={{ delay: 0.15, type: "spring", stiffness: 300, damping: 30 }}
              className="fixed bottom-20 z-[74] pointer-events-none"
              style={{
                left: isDesktopBottomDockOpen ? `${panelHeight + 16}px` : '16px',
                transition: 'left 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', // Spring-like animation for left position
              }}
            >
              <div className="bg-white/95 dark:bg-gray-800/95 border-2 border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 md:px-3 md:py-1.5 shadow-lg backdrop-blur-md">
                <div className="text-xs md:text-sm text-gray-800 dark:text-gray-200 font-semibold font-zonaProUltra whitespace-nowrap">
                  {userScreenName}
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Mobile: Between Main Dock and Bottom Dock */}
          {isMobile && isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, type: "spring" }}
              className="fixed left-0 pl-4 z-[65] pointer-events-none flex items-center justify-center"
              data-dock-container
              style={{
                width: isSmallMobile ? 48 : Math.max(panelHeight * 0.9, 60),
                // Calculate position relative to bottom dock:
                // Bottom dock structure: margin-bottom + padding-top + items + gaps + padding-bottom
                // Item sizes: small mobile 38px, regular mobile 50px
                // Gaps: small mobile 4px (gap-1), regular mobile 8px (gap-2)
                bottom: `${(isSmallMobile ? 4 : 8) + (isSmallMobile ? 4 : 8) + (bottomNavigationItems.length * (isSmallMobile ? 38 : Math.max(baseItemSize * 0.9, 50))) + ((bottomNavigationItems.length - 1) * (isSmallMobile ? 4 : 8)) + (isSmallMobile ? 4 : 8) + (isSmallMobile ? 4 : 8)}px`
              }}
            >
              <div className="bg-white/95 dark:bg-gray-800/95 border-2 border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 md:px-3 md:py-1.5 shadow-lg backdrop-blur-md ml-2">
                <div className="text-xs md:text-sm text-gray-800 dark:text-gray-200 font-semibold font-zonaProUltra whitespace-nowrap">
                  {userScreenName}
                </div>
              </div>
            </motion.div>
          )}
        </>
      )}

      {/* Desktop Bottom Dock Menu Button - Only for non-mobile screens */}
      {!isMobile && shouldShowDock && (
        <motion.button
          onClick={() => setIsDesktopBottomDockOpen(!isDesktopBottomDockOpen)}
          className="fixed bottom-4 z-[75] p-3 rounded-xl bg-white/95 dark:bg-gray-800/95 border-2 border-gray-300 dark:border-gray-600 shadow-lg backdrop-blur-md flex items-center justify-center"
          style={{
            left: isDesktopBottomDockOpen ? `${panelHeight + 16}px` : '16px', // Move to right of dock when open
          }}
          initial={false}
          animate={{
            left: isDesktopBottomDockOpen ? `${panelHeight + 16}px` : '16px',
          }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Toggle bottom menu"
        >
          <AnimatePresence mode="wait">
            {isDesktopBottomDockOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <X size={24} className="text-gray-700 dark:text-gray-300" />
              </motion.div>
            ) : (
              <motion.div
                key="menu"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Menu size={24} className="text-gray-700 dark:text-gray-300" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      )}

      {/* Bottom Dock - Left Bottom */}
      {((isMobile && isMobileMenuOpen) || (!isMobile && isDesktopBottomDockOpen)) && (
        <motion.div 
          className="fixed left-0 bottom-0 flex items-end justify-center z-[70] pointer-events-none"
          data-dock-container
          initial={isMobile ? { x: -300 } : { y: 300 }}
          animate={isMobile ? { x: isMobileMenuOpen ? 0 : -300 } : { y: isDesktopBottomDockOpen ? 0 : 300 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          onClick={(e) => {
            // Prevent clicks from bubbling to overlay
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            // Prevent touch events from bubbling to overlay
            e.stopPropagation();
          }}
        >
          <motion.div
            onMouseMove={({ pageY }) => {
              if (!isMobile) {
                bottomIsHovered.set(1);
                bottomMouseY.set(pageY);
              }
            }}
            onMouseLeave={() => {
              if (!isMobile) {
                bottomIsHovered.set(0);
                bottomMouseY.set(Infinity);
              }
            }}
            onClick={(e) => {
              // Prevent clicks from bubbling to overlay
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              // Prevent touch events from bubbling to overlay
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              // Prevent touch events from bubbling to overlay
              e.stopPropagation();
            }}
            className={`${className} pointer-events-auto flex flex-col items-center ${isSmallMobile ? 'gap-1' : 'gap-2'} md:gap-3 rounded-tr-2xl md:rounded-2xl border-t-2 border-r-2 md:border-2 border-gray-300 dark:border-gray-600 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md ${isSmallMobile ? 'py-1 px-0.5' : 'py-2 px-1'} md:py-4 md:px-2 shadow-xl md:ml-2 ${isSmallMobile ? 'mb-1' : 'mb-2'} md:mb-4 transition-all duration-300 overflow-visible`}
            style={{ 
              width: isMobile ? (isSmallMobile ? 48 : Math.max(panelHeight * 0.9, 60)) : panelHeight,
              minWidth: isMobile ? (isSmallMobile ? 48 : Math.max(panelHeight * 0.9, 60)) : panelHeight,
              pointerEvents: 'auto',
              zIndex: 70
            }}
            role="toolbar"
            aria-label="Bottom application dock"
          >
            {bottomNavigationItems.map((item, index) => (
              <DockItem
                key={`bottom-${index}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (item.onClick) {
                    item.onClick(e);
                  }
                  if (isMobile) {
                    setTimeout(() => setIsMobileMenuOpen(false), 200);
                  } else {
                    // Close desktop bottom dock after clicking an item
                    setTimeout(() => setIsDesktopBottomDockOpen(false), 200);
                  }
                }}
                className={item.className || (item.active ? `ring-2 ${isDarkMode ? 'ring-[#ff7a00]' : 'ring-[#212D82]'}` : '')}
                mouseY={bottomMouseY}
                spring={spring}
                distance={distance}
                magnification={isMobile ? (isSmallMobile ? baseItemSize * 1.2 : baseItemSize * 1.35) : magnification}
                baseItemSize={isMobile ? (isSmallMobile ? 38 : Math.max(baseItemSize * 0.9, 50)) : baseItemSize}
                isMobile={isMobile}
              >
                <DockIcon>{item.icon}</DockIcon>
                <DockLabel extraMargin={item.label === 'Subscription' || item.label === 'Logout'}>{item.label}</DockLabel>
              </DockItem>
            ))}
          </motion.div>
        </motion.div>
      )}

      {/* Logout Modal */}
      {promptLogout && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl shadow-xl px-8 py-8 w-full max-w-md mx-4 border border-white/20 dark:border-gray-700/50">
            <div className="text-center mb-6">
              <h2
                className="text-2xl md:text-3xl font-bold font-zonaProUltra relative inline-block mb-4"
                style={{
                  color: isDarkMode ? '#ff7a00' : '#212D82',
                  background: sectionHeadingGradient,
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shine 10s linear infinite'
                }}
              >
                Log Out?
              </h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-8 text-center font-zonaProRegular">
              Are you sure you want to log out of your account?
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setPromptLogout(false)}
                className="px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-full font-zonaProUltra text-gray-700 dark:text-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-full font-zonaProUltra transition-colors"
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </>
  );
}
